import type { ReactNode } from 'react';
import { Callout } from './Callout';
import { Modal } from './Modal';
import { Spinner } from './Spinner';

/**
 * Generic destructive-action confirmation. The parent owns the mutation and
 * passes `busy` / `error` so the dialog can reflect progress and failures.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  confirmClassName = 'btn-danger',
  busy = false,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** CSS class(es) for the confirm button. Defaults to 'btn-danger'. */
  confirmClassName?: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      widthClass="max-w-md"
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={confirmClassName}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-ink-muted">{message}</div>
      {error && (
        <div className="mt-3">
          <Callout tone="danger">{error}</Callout>
        </div>
      )}
    </Modal>
  );
}
